import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getInstanceConfig } from '../services/InstanceService';

const InstanceContext = createContext();

export const useInstance = () => {
  const context = useContext(InstanceContext);
  if (!context) {
    throw new Error('useInstance must be used within an InstanceProvider');
  }
  return context;
};

export const InstanceProvider = ({ children }) => {
  const [memberId, setMemberId] = useState(null);
  const [memberName, setMemberName] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkConfiguration = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getInstanceConfig();

      if (response.data.success && response.data.configured) {
        setMemberId(response.data.memberId);
        setMemberName(response.data.memberName || response.data.memberEName);
        setIsConfigured(true);
      } else {
        setIsConfigured(false);
        setMemberId(null);
        setMemberName(null);
      }
    } catch (err) {
      console.error('Failed to check instance config:', err);
      setError(err.message || 'Failed to load configuration');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkConfiguration();
  }, [checkConfiguration]);

  const updateMemberId = (newMemberId, newMemberName) => {
    setMemberId(newMemberId);
    setMemberName(newMemberName);
    setIsConfigured(true);
  };

  const resetConfiguration = () => {
    setMemberId(null);
    setMemberName(null);
    setIsConfigured(false);
  };

  return (
    <InstanceContext.Provider
      value={{
        memberId,
        memberName,
        isConfigured,
        isLoading,
        error,
        updateMemberId,
        resetConfiguration,
        checkConfiguration,
      }}
    >
      {children}
    </InstanceContext.Provider>
  );
};

export default InstanceContext;
